/**
 * Error Message Utility for Web Admin Portal
 * 
 * Provides user-friendly error messages that don't expose internal system details.
 */

const IS_DEVELOPMENT = import.meta.env.DEV;

/**
 * Get user-friendly error message based on error type
 */
export function getUserFriendlyError(error: any, context = ''): string {
  // Network errors
  if (error.message === 'Network request failed' || error.code === 'NETWORK_ERROR') {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  // Timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'The request took too long. Please try again.';
  }
  
  // Authentication errors
  if (error.code === 'NotAuthorizedException' || error.message?.includes('Incorrect username or password')) {
    return 'Incorrect email or password. Please try again.';
  }
  
  if (error.code === 'UserNotFoundException') {
    return 'No account found with this email address.';
  }
  
  if (error.code === 'UsernameExistsException') {
    return 'An account with this email already exists.';
  }
  
  if (error.code === 'InvalidPasswordException') {
    return 'Password must be at least 8 characters and include uppercase, lowercase, and numbers.';
  }
  
  // Authorization errors
  if (error.status === 401 || error.code === 'UNAUTHORIZED') {
    return 'Your session has expired. Please sign in again.';
  }
  
  if (error.status === 403 || error.code === 'FORBIDDEN') {
    return 'You don\'t have permission to perform this action.';
  }

  // Validation errors
  if (error.status === 400 || error.code === 'VALIDATION_ERROR') {
    if (error.message && !containsSensitiveInfo(error.message)) {
      return error.message;
    }
    return 'Please check your input and try again.';
  }
  
  // Not found errors
  if (error.status === 404 || error.code === 'NOT_FOUND') {
    return `${context || 'Item'} not found.`;
  }
  
  // Conflict errors
  if (error.status === 409 || error.code === 'CONFLICT') {
    return 'This item already exists or conflicts with existing data.';
  }
  
  // Server errors
  if (error.status >= 500 || error.code === 'SERVER_ERROR') {
    return 'A server error occurred. Please try again later.';
  }
  
  // Rate limiting
  if (error.status === 429 || error.code === 'RATE_LIMIT') {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  // Default error message
  if (IS_DEVELOPMENT && error.message && !containsSensitiveInfo(error.message)) {
    return `Error: ${error.message}`;
  }
  
  return 'An unexpected error occurred. Please try again.';
}

function containsSensitiveInfo(message: string): boolean {
  if (typeof message !== 'string') return false;
  
  const sensitivePatterns = [
    /password/i, /token/i, /secret/i, /key/i, /api[_-]?key/i,
    /auth/i, /credential/i, /database/i, /sql/i, /query/i,
    /stack trace/i, /file path/i, /\/.+\//, /[a-f0-9]{32,}/i,
  ];
  
  return sensitivePatterns.some(pattern => pattern.test(message));
}

export const ErrorMessages = {
  LOGIN_FAILED: 'Unable to sign in. Please check your email and password.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  REGISTRATION_FAILED: 'Unable to create account. Please try again.',
  EMAIL_ALREADY_EXISTS: 'An account with this email already exists.',
  STUDENT_CREATE_FAILED: 'Unable to create student. Please try again.',
  STUDENT_UPDATE_FAILED: 'Unable to update student. Please try again.',
  STUDENT_DELETE_FAILED: 'Unable to delete student. Please try again.',
  CLASS_CREATE_FAILED: 'Unable to create class. Please try again.',
  CLASS_UPDATE_FAILED: 'Unable to update class. Please try again.',
  CLASS_DELETE_FAILED: 'Unable to delete class. Please try again.',
  WORDLIST_CREATE_FAILED: 'Unable to create word list. Please try again.',
  WORDLIST_UPDATE_FAILED: 'Unable to update word list. Please try again.',
  WORDLIST_DELETE_FAILED: 'Unable to delete word list. Please try again.',
  TEACHER_CREATE_FAILED: 'Unable to create teacher account. Please try again.',
  PARENT_CREATE_FAILED: 'Unable to create parent account. Please try again.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  SERVER_ERROR: 'A server error occurred. Please try again later.',
  PERMISSION_DENIED: 'You don\'t have permission to perform this action.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};
